import { type CommonData, Country } from "../types/common";

type Person = {
  firstName?: string;
  lastName?: string;
};

const ADDRESS_NOT_AVAILABLE = "Address not available";

/**
 * Resolves the applicant's registered address, formatted as
 * "street, postcode, city", from the country-specific integration:
 * UC for Sweden, Bisnode DVV Finance for Finland. Returns a fallback
 * string when no address is available.
 */
export const getApplicantAddress = (data: CommonData): string => {
  if (data?.country === Country.SE) {
    const addressInformation = data?.integrations?.uc?.addressInformation;
    return addressInformation
      ? `${addressInformation.streetName}, ${addressInformation.postcode}, ${addressInformation.municipality}`
      : ADDRESS_NOT_AVAILABLE;
  }

  const addressInformation =
    data?.integrations?.bisnode?.dvvFinance?.applicant?.response
      ?.consumerInformation?.registeredAddresses?.[0];
  return addressInformation
    ? `${addressInformation.streetAddress}, ${addressInformation.postalCode}, ${addressInformation.postOffice}`
    : ADDRESS_NOT_AVAILABLE;
};

type LegalName = {
  firstName?: string;
  familyName?: string;
};

/**
 * Resolves a person's legal name from the population register report, like
 * the backend's getFirstName/getLastName: the Bisnode report for Sweden, and
 * for Finland the Bisnode DVV Finance report with the payment-remark report as
 * fallback. Returns undefined for other countries.
 */
export const getRegisteredLegalName = (
  data: CommonData,
  isMainApplicant?: boolean
): LegalName | undefined => {
  const participant = isMainApplicant ? "applicant" : "coapplicant";
  const bisnode = data?.integrations?.bisnode;

  if (data?.country === Country.SE) {
    return bisnode?.[participant]?.response?.consumerInformation?.legalName;
  }
  if (data?.country === Country.FI) {
    return (
      bisnode?.dvvFinance?.[participant]?.response?.consumerInformation
        ?.legalName ??
      bisnode?.paymentRemark?.[participant]?.response?.consumerInformation
        ?.legalName
    );
  }
  return undefined;
};

export const updatePersonName = (
  data: CommonData,
  targetPerson?: Person,
  isMainApplicant?: boolean
): void => {
  if (!data || !targetPerson) {
    return;
  }

  const manualChangedValues = data?.caseWork?.["manual-changed-values"] ?? {};
  const manualChangedValuesPerson = {
    firstName: isMainApplicant
      ? manualChangedValues?.firstName
      : manualChangedValues?.firstNameCoapplicant,
    lastName: isMainApplicant
      ? manualChangedValues?.lastName
      : manualChangedValues?.lastNameCoapplicant
  };

  // SE and FI get from Bisnode
  if (data?.country === Country.SE || data?.country === Country.FI) {
    const legalName = getRegisteredLegalName(data, isMainApplicant);

    targetPerson.firstName =
      manualChangedValuesPerson?.firstName ||
      legalName?.firstName ||
      targetPerson.firstName ||
      "";
    targetPerson.lastName =
      manualChangedValuesPerson?.lastName ||
      legalName?.familyName ||
      targetPerson.lastName ||
      "";
  } else if (data?.country === "NO") {
    // NO get from Experian
    // TODO: Get data from Experian integration, update paths when available
    const experianData = data?.integrations?.experian || {};
    const experianFirstName = isMainApplicant
      ? experianData?.applicant?.firstName
      : experianData?.coapplicant?.firstName;
    const experianFamilyName = isMainApplicant
      ? experianData?.applicant?.lastName
      : experianData?.coapplicant?.lastName;

    targetPerson.firstName =
      manualChangedValuesPerson?.firstName ||
      experianFirstName ||
      targetPerson.firstName ||
      "";
    targetPerson.lastName =
      manualChangedValuesPerson?.lastName ||
      experianFamilyName ||
      targetPerson.lastName ||
      "";
  }
};
